import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

interface FAQItemProps {
  question: string;
  answer: string | React.ReactNode;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl mb-4 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      whileHover={{ scale: 1.01 }}
      layout
    >
      <button
        className="w-full flex justify-between items-center text-left px-6 py-4 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-semibold text-white text-lg pr-4">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="flex-shrink-0"
        >
          <ChevronDownIcon className="w-5 h-5 text-blue-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="px-6 py-4 bg-white/5 text-gray-200 leading-relaxed border-t border-white/10">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Updated FAQ data based on instructions
const faqData = {
  general: [
    {
      q: 'What is the Trading Dashboard?',
      a: 'It is a specialized P&L (Profit and Loss) tracking dashboard designed for monitoring the performance of your cryptocurrency trading bots, primarily interacting with Bybit via API.',
    },
    {
      q: 'Which exchanges are supported?',
      a: (
        <div className="space-y-3">
          <p>We currently support three major cryptocurrency futures exchanges:</p>
          <div className="space-y-2 ml-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="font-semibold text-blue-300">Bybit</span>
              <span className="text-xs text-green-400">(USDT-M Futures)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="font-semibold text-yellow-300">Binance</span>
              <span className="text-xs text-green-400">(USDT-M Futures)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="font-semibold text-purple-300">Bitget</span>
              <span className="text-xs text-green-400">(USDT-M Futures)</span>
            </div>
          </div>
          <p className="text-sm text-gray-400 italic mt-2">All exchanges require read-only API access with support for USDT-margined perpetual futures.</p>
        </div>
      ),
    },
    {
      q: 'What are the main features?',
      a: 'The dashboard allows you to view detailed trade metrics, track historical performance of different bot strategies, analyze P&L across accounts, and visualize data using interactive charts.',
    },
  ],
  billing: [
    {
      q: 'How does the subscription work?',
      a: 'We offer monthly subscription plans (e.g., Basic, Standard, Elite) managed via Auth0 roles. Payments are processed securely through Stripe.',
    },
    {
      q: 'Can I manage my subscription?',
      a: 'Yes, you can typically manage your subscription details, view billing history, and cancel your plan through your account settings page. Access continues until the end of the current paid period after cancellation.',
    },
    {
      q: 'What payment methods are accepted?',
      a: 'We accept major credit cards processed securely via Stripe.',
    },
  ],
  technical: [
    {
      q: 'How is my data secured?',
      a: 'Authentication is handled securely by Auth0. Exchange API keys are stored securely and used by the backend (FastAPI) to fetch data. We prioritize industry-standard security practices.',
    },
    {
      q: 'What technology stack is used?',
      a: 'The frontend is built with React (using Vite) and styled with Tailwind CSS. The backend is a Python FastAPI application, and the database is PostgreSQL (managed via Supabase).',
    },
    {
      q: 'Is there an API available?',
      a: 'The frontend communicates with a backend RESTful API built with FastAPI. Direct API access for users might be available depending on the subscription plan (e.g., Elite plan).',
    },
    {
      q: 'What are the technical differences between exchanges?',
      a: (
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-2 px-3 font-semibold">Capability</th>
                  <th className="text-center py-2 px-3 text-blue-300">Bybit</th>
                  <th className="text-center py-2 px-3 text-yellow-300">Binance</th>
                  <th className="text-center py-2 px-3 text-purple-300">Bitget</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <tr><td className="py-2 px-3">API Version</td><td className="py-2 px-3 text-center">V5</td><td className="py-2 px-3 text-center">V1/V2</td><td className="py-2 px-3 text-center">V2</td></tr>
                <tr><td className="py-2 px-3">Passphrase Required</td><td className="py-2 px-3 text-center">❌</td><td className="py-2 px-3 text-center">❌</td><td className="py-2 px-3 text-center">✅</td></tr>
                <tr><td className="py-2 px-3">Time Sync</td><td className="py-2 px-3 text-center">✅ Unified</td><td className="py-2 px-3 text-center">✅ Unified</td><td className="py-2 px-3 text-center">✅ Unified</td></tr>
                <tr><td className="py-2 px-3">P&L Endpoint</td><td className="py-2 px-3 text-center">Dedicated</td><td className="py-2 px-3 text-center">Trade Fills</td><td className="py-2 px-3 text-center">Trade Fills</td></tr>
                <tr><td className="py-2 px-3">Position Data</td><td className="py-2 px-3 text-center">Aggregated</td><td className="py-2 px-3 text-center">Per Fill</td><td className="py-2 px-3 text-center">Per Fill</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg p-2">All exchanges use the same unified time synchronization module and provide 100% accurate P&L tracking for your trading dashboard.</p>
        </div>
      ),
    },
  ],
};

const FAQPage = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="mb-6">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-lg border border-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <QuestionMarkCircleIcon className="w-10 h-10 text-blue-400" />
            </div>
          </motion.div>
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text leading-tight pb-1 mb-4"
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Find answers to common questions about our trading dashboard and services
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <motion.section variants={itemVariants}>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 mb-6">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">G</span>
                </div>
                General
              </h2>
              <div className="space-y-4">
                {faqData.general.map((item, index) => (
                  <FAQItem key={`gen-${index}`} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section variants={itemVariants}>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 mb-6">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">$</span>
                </div>
                Billing & Subscriptions
              </h2>
              <div className="space-y-4">
                {faqData.billing.map((item, index) => (
                  <FAQItem key={`bill-${index}`} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section variants={itemVariants}>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">⚡</span>
                </div>
                Technical
              </h2>
              <div className="space-y-4">
                {faqData.technical.map((item, index) => (
                  <FAQItem key={`tech-${index}`} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          </motion.section>
        </motion.div>

        {/* Contact section */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Still have questions?</h3>
            <p className="text-gray-300 mb-6">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <a 
              href="mailto:support@botblender.com"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQPage;
